import { useEffect, useRef, useState } from "react";

const VERSION_CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

const fetchBuildId = async (): Promise<string | undefined> => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    const { buildId } = await response.json();
    return buildId;
  } catch (err: any) {
    console.error(err.message);
    return undefined;
  }
};

// Detects when this tab's already-loaded bundle is behind what's currently
// deployed, by comparing the buildId captured on mount against fresh fetches
// of public/version.json (regenerated on every `npm run build`).
export const useCheckWebAppVersion = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialBuildId = useRef<string | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;

    const checkVersion = async () => {
      const buildId = await fetchBuildId();
      if (!buildId || !isMounted) {
        return;
      }
      if (initialBuildId.current === undefined) {
        initialBuildId.current = buildId;
      } else if (buildId !== initialBuildId.current) {
        setUpdateAvailable(true);
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL_MS);

    // visibilitychange only fires when the tab itself is hidden (minimized,
    // covered, backgrounded tab) - switching to another app while this
    // window stays visible on screen doesn't trigger it in most browsers.
    // window focus fires on any focus change, so listen for both.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", checkVersion);

    return () => {
      isMounted = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", checkVersion);
    };
  }, []);

  return updateAvailable;
};
